import type { BrandVariants } from '@fluentui/react-components';

/**
 * THE ONE COLOUR FILE. Every brand colour in the design system is defined here and
 * nowhere else — swap these ramps and the whole platform re-skins. Nothing outside this
 * file may hard-code a hex value.
 *
 * The two accents are a safety control, not decoration: they are what stops someone
 * emailing a customer from the wrong brand. They therefore sit on opposite sides of the
 * blue–yellow axis (teal vs amber), which stays distinguishable under red–green colour
 * vision deficiency, rather than the blue-vs-red pair that collapses for many people.
 *
 * Ramps follow Fluent's 16-step BrandVariants contract: 10 darkest, 160 lightest.
 * Shade 80 is the light-theme fill, 110 the dark-theme accent. Both are contrast-checked:
 *   talt.80        vs white   5.60:1
 *   royalAura.80   vs white   5.50:1
 *   talt.110       vs #292929 6.66:1
 *   royalAura.110  vs #292929 6.60:1
 */

/** Talt — cool deep teal. Hue 190. */
export const taltBrandRamp: BrandVariants = {
  10: '#041316',
  20: '#061e23',
  30: '#0a2c33',
  40: '#0e3b44',
  50: '#124954',
  60: '#175663',
  70: '#1d6472',
  80: '#227181',
  90: '#3499ad',
  100: '#53b3c6',
  110: '#6cbbcb',
  120: '#84c5d2',
  130: '#a1cfd9',
  140: '#c0dde3',
  150: '#dae9ec',
  160: '#eff4f6',
};

/** Royal Aura — warm muted amber. Hue 36. */
export const royalAuraBrandRamp: BrandVariants = {
  10: '#1c1203',
  20: '#291b05',
  30: '#3b2607',
  40: '#4c320a',
  50: '#5d3d0e',
  60: '#6e4912',
  70: '#7e5416',
  80: '#8d601b',
  90: '#b87f28',
  100: '#d19a47',
  110: '#d5a762',
  120: '#dab47c',
  130: '#dfc49a',
  140: '#e7d6bb',
  150: '#efe5d7',
  160: '#f7f3ee',
};

/**
 * The neutral chrome both brands share: page, surface, text, borders. Only the accent
 * distinguishes one brand from the other, so everything here is brand-independent.
 *
 * Dark is not a tint of light — `page` and `surface` swap which one recedes, and the two
 * are close enough that the card is separated by `rule`, not by fill.
 */
export const chrome = {
  light: {
    page: '#edf1ef',
    surface: '#f8faf9',
    text: '#16211e',
    muted: '#5e6f69',
    rule: '#c7d2ce',
  },
  dark: {
    page: '#16211e',
    surface: '#161e1b',
    text: '#edf1ef',
    muted: '#90a099',
    rule: '#2b3733',
  },
} as const;

/** One stack everywhere. Segoe UI first on Windows keeps us aligned with Fluent. */
export const fontStack =
  '-apple-system, BlinkMacSystemFont, "Segoe UI Variable Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const brandIds = ['royalaura', 'talt'] as const;
export type Brand = (typeof brandIds)[number];

export type BrandDefinition = {
  id: Brand;
  /** Customer-facing name. Always shown in full — never abbreviate in a safety surface. */
  name: string;
  domain: string;
  ramp: BrandVariants;
};

export const brands: Record<Brand, BrandDefinition> = {
  royalaura: {
    id: 'royalaura',
    name: 'Royal Aura',
    domain: 'royalaura.ae',
    ramp: royalAuraBrandRamp,
  },
  talt: {
    id: 'talt',
    name: 'Talt',
    domain: 'talt.ae',
    ramp: taltBrandRamp,
  },
};

export const isBrand = (value: unknown): value is Brand =>
  typeof value === 'string' && (brandIds as readonly string[]).includes(value);

/** Resolve the brand that owns an address, by domain suffix. */
export const brandForAddress = (address: string): Brand | undefined => {
  const domain = address.split('@')[1]?.toLowerCase();
  if (!domain) return undefined;
  return brandIds.find((id) => domain === brands[id].domain);
};
